import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Users, 
  FileText, 
  TrendingUp, 
  Clock, 
  Zap,
  Calendar,
  Euro,
  BarChart3,
  CheckCircle,
  XCircle,
  Sun,
  Package
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [newEndDate, setNewEndDate] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenewed = (alert) => {
    setSelectedContract(alert);
    setNewEndDate("");
    setShowRenewalModal(true);
  };

  const handleNotRenewed = async (alert) => {
    if (!confirm("Confirmer que ce contrat ne sera pas renouvelé ?")) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${alert.contract_id}/renewal/not-renewed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Contrat marqué comme non renouvelé");
      fetchDashboardData(); // Rafraîchir les données
    } catch (error) {
      console.error("Error marking contract as not renewed:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleConfirmRenewal = async () => {
    if (!newEndDate) {
      toast.error("Veuillez saisir une nouvelle date de fin");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.contract_id}/renewal/renewed`,
        { new_end_date: newEndDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Contrat renouvelé avec succès !");
      setShowRenewalModal(false);
      fetchDashboardData(); // Rafraîchir les données
    } catch (error) {
      console.error("Error renewing contract:", error);
      toast.error("Erreur lors du renouvellement");
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-800 border-red-200",
      medium: "bg-amber-100 text-amber-800 border-amber-200",
      low: "bg-blue-100 text-blue-800 border-blue-200"
    };
    return colors[priority] || colors.medium;
  };

  const formatDaysRemaining = (days) => {
    if (days <= 0) return "Expiré";
    if (days === 1) return "Demain";
    return `${days} jours`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-12 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Vue d'ensemble de votre activité SUNLIFE</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          <span>Dernière mise à jour: {new Date().toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: 'short', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover-lift" data-testid="stats-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData?.stats?.total_clients || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift" data-testid="stats-projets">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Projets Actifs</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData?.stats?.projets_actifs || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift" data-testid="stats-aides">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Dossiers Aides</p>
                <p className="text-3xl font-bold text-green-600">{dashboardData?.stats?.dossiers_aides || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift" data-testid="stats-interventions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Interventions SAV</p>
                <p className="text-3xl font-bold text-amber-600">{dashboardData?.stats?.interventions_sav || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Alerts */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span>Alertes & Notifications</span>
            </CardTitle>
            <CardDescription>
              Projets et interventions nécessitant une attention
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboardData?.alerts && dashboardData.alerts.length > 0 ? (
              dashboardData.alerts.slice(0, 5).map((alert, index) => (
                <div 
                  key={alert.id || index}
                  className={`p-4 rounded-lg border ${getPriorityColor(alert.priority)} ${alert.priority}-priority`}
                  data-testid={`alert-${index}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{alert.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {formatDaysRemaining(alert.days_remaining)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge variant="outline" className={getPriorityColor(alert.priority)}>
                        {alert.priority}
                      </Badge>
                      
                      {/* Boutons pour les alertes de renouvellement */}
                      {alert.contract_id && (
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                            onClick={() => handleRenewed(alert)}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Renouvelé
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                            onClick={() => handleNotRenewed(alert)}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Non renouvelé
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune alerte récente</p>
                <p className="text-sm">Tous vos contrats sont à jour !</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions - Visible uniquement pour Super Admin et Administrateur */}
        {currentUser && (currentUser.is_super_admin || currentUser.role === 'admin' || currentUser.role === 'administratif') && (
          <Card className="hover-lift">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <span>Actions Rapides</span>
              </CardTitle>
              <CardDescription>
                Raccourcis vers les fonctionnalités principales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
            <Button 
              className="w-full justify-start h-auto p-4 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
              variant="outline"
              data-testid="quick-add-projet"
              onClick={() => navigate('/projets')}
            >
              <Sun className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Nouveau Projet</div>
                <div className="text-sm opacity-75">Créer un projet d'installation</div>
              </div>
            </Button>

            <Button 
              className="w-full justify-start h-auto p-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
              variant="outline"
              data-testid="quick-add-client"
              onClick={() => navigate('/clients')}
            >
              <Users className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Ajouter un Client</div>
                <div className="text-sm opacity-75">Créer une nouvelle fiche client</div>
              </div>
            </Button>

            <Button 
              className="w-full justify-start h-auto p-4 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
              variant="outline"
              data-testid="quick-simulate"
              onClick={() => navigate('/simulator')}
            >
              <BarChart3 className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Simulateur Solaire</div>
                <div className="text-sm opacity-75">Calculer rendement & aides</div>
              </div>
            </Button>

            <Button 
              className="w-full justify-start h-auto p-4 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200"
              variant="outline"
              data-testid="quick-produits"
              onClick={() => navigate('/produits')}
            >
              <Package className="w-5 h-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Catalogue Produits</div>
                <div className="text-sm opacity-75">Gérer le stock et tarifs</div>
              </div>
            </Button>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Performance Overview - Données Réelles */}
      {/* Masqué pour les rôles COMMERCIAL et RÉGIE */}
      {currentUser && currentUser.role !== 'commercial' && currentUser.role !== 'regie' && (
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span>Performance du Mois</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.performance ? (
              <div className="grid gap-6 md:grid-cols-3">
                <div className="text-center p-6 bg-emerald-50 rounded-lg">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">
                    {dashboardData.performance.new_clients_this_month || 0}
                  </div>
                  <div className="text-sm text-emerald-700 font-medium">Nouveaux Clients</div>
                  <div className={`text-xs ${dashboardData.performance.new_clients_pct_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {dashboardData.performance.new_clients_pct_change >= 0 ? '+' : ''}
                    {dashboardData.performance.new_clients_pct_change}% vs mois dernier
                  </div>
                </div>
                <div className="text-center p-6 bg-red-50 rounded-lg">
                  <div className="text-3xl font-bold text-red-600 mb-2">
                    {dashboardData.performance.projets_termines_this_month || 0}
                  </div>
                  <div className="text-sm text-red-700 font-medium">Projets Terminés</div>
                  <div className="text-xs text-red-600">
                    {dashboardData.performance.projets_en_cours || 0} en cours
                  </div>
                </div>
                <div className="text-center p-6 bg-amber-50 rounded-lg">
                  <div className="text-3xl font-bold text-amber-600 mb-2">
                    €{(dashboardData.performance.revenue_this_month || 0).toLocaleString('fr-FR')}
                  </div>
                  <div className="text-sm text-amber-700 font-medium">CA du Mois</div>
                  <div className={`text-xs ${dashboardData.performance.revenue_pct_change >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                    {dashboardData.performance.revenue_pct_change >= 0 ? '+' : ''}
                    {dashboardData.performance.revenue_pct_change}% vs mois dernier
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune donnée disponible</p>
                <p className="text-sm">Commencez par ajouter des clients et des contrats</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal de renouvellement */}
      <Dialog open={showRenewalModal} onOpenChange={setShowRenewalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renouveler le Contrat</DialogTitle>
            <DialogDescription>
              {selectedContract && `Contrat arrivant à échéance le ${new Date(selectedContract.end_date).toLocaleDateString('fr-FR')}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new_end_date">Nouvelle Date de Fin</Label>
              <Input
                id="new_end_date"
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Cette date sera utilisée pour la prochaine alerte de renouvellement
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRenewalModal(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirmRenewal}
              className="bg-green-600 hover:bg-green-700"
            >
              Confirmer le Renouvellement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;